import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiClient } from '../utils/api';

type ContactCategory = 'general' | 'technical' | 'billing' | 'feedback' | 'demo';

const ALLOWED_CATEGORIES: ContactCategory[] = ['general', 'technical', 'billing', 'feedback', 'demo'];

const getInitialCategory = (search: string): ContactCategory => {
  const category = new URLSearchParams(search).get('category');
  return category && ALLOWED_CATEGORIES.includes(category as ContactCategory)
    ? (category as ContactCategory)
    : 'general';
};

const ContactSupportPage: React.FC = () => {
  const location = useLocation();
  const isPublic = !location.pathname.startsWith('/dashboard');
  const backTo = isPublic ? '/' : '/dashboard';
  const backLabel = isPublic ? '← Back to Home' : '← Back';
  const initialCategory = getInitialCategory(location.search);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: initialCategory
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const categoryFromQuery = getInitialCategory(location.search);
    setFormData((prev) => ({ ...prev, category: categoryFromQuery }));
  }, [location.search]);

  useEffect(() => {
    if (!error && !success) return;
    const timer = setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [error, success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim() || undefined,
        email: formData.email.trim().toLowerCase(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        category: formData.category,
      };
      await apiClient.post('/api/v1/support/contact', payload);
      setSuccess('Your message has been sent. Our support team will contact you shortly.');
      setFormData(prev => ({
        name: prev.name,
        email: prev.email,
        subject: '',
        message: '',
        category: initialCategory,
      }));
    } catch (err: any) {
      setError(err?.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="static-page">
      <Link to={backTo} className="static-page-back">{backLabel}</Link>
      <h1 className="static-page-title">Contact Support</h1>
      <p className="static-page-lead">
        Have a question or need help? Send us a message and we’ll get back to you as soon as possible.
      </p>

      <section className="static-page-section static-page-contact">
        <div className="contact-info">
          <h3>Support channels</h3>
          <ul>
            <li><strong>Email:</strong> support@arihex.com</li>
          </ul>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
            {error && <div className="app-error">{error}</div>}
            {success && <div className="app-success">{success}</div>}
            <div className="form-group">
              <label htmlFor="name">Your name (optional)</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Your email address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="general">General inquiry</option>
                <option value="technical">Technical support</option>
                <option value="billing">Billing</option>
                <option value="feedback">Feedback</option>
                <option value="demo">Demo request</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Brief subject line"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Describe your question or issue..."
                rows={6}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send message'}
            </button>
          </form>
      </section>
    </div>
  );
};

export default ContactSupportPage;
